import { NetRandom } from '../../core/rng/NetRandom.js';

/**
 * Every random number the visual side uses, and where it came from.
 *
 * A renderer calling `Math.random()` is the difference between an artwork that
 * can be re-rendered at 4K next week and one that existed for four minutes. So
 * renderers are not given a random function; they are given one of these, and
 * the engine decides what seeds it.
 *
 * Streams are **derived by name**, not shared. If a layer took numbers from one
 * global sequence, adding a second layer would shift every draw the first one
 * made and the same recipe would stop reproducing. `derive('MATH:2')` gives that
 * layer its own sequence, from the same root seed, that no other layer can move.
 *
 * The generator underneath is `NetRandom` — already in the project because the
 * chart system needs .NET's exact sequence for Unity parity. Reusing it here is
 * not about parity, it is about not having a second PRNG to reason about.
 */
export class VisualRandom {
  private readonly source: NetRandom;
  private drawn = 0;

  constructor(readonly seed: number) {
    this.source = new NetRandom(seed | 0);
  }

  /** 0..1. */
  next(): number {
    this.drawn += 1;
    return this.source.nextDouble();
  }

  /** `min` inclusive, `max` exclusive. */
  range(min: number, max: number): number {
    return min + this.next() * (max - min);
  }

  int(minInclusive: number, maxExclusive: number): number {
    if (maxExclusive <= minInclusive) return minInclusive;
    return minInclusive + Math.floor(this.next() * (maxExclusive - minInclusive));
  }

  /** -1..1, which is what most placement and jitter actually wants. */
  signed(): number {
    return this.next() * 2 - 1;
  }

  pick<T>(values: readonly T[]): T {
    if (values.length === 0) throw new RangeError('pick from an empty list');
    return values[this.int(0, values.length)] as T;
  }

  bool(probability = 0.5): boolean {
    return this.next() < probability;
  }

  /**
   * A child stream, reproducible from this seed and that name.
   *
   * The hash is FNV-1a, chosen because it is short, has no dependencies and
   * spreads similar names — `MANDALA:0` and `MANDALA:1` must not land on
   * neighbouring seeds, or two layers of the same renderer would move together.
   */
  derive(name: string): VisualRandom {
    return new VisualRandom(mixSeed(this.seed, name));
  }

  /** How many numbers have been taken. Used to show a stream diverging. */
  get drawCount(): number {
    return this.drawn;
  }
}

export function mixSeed(seed: number, name: string): number {
  let hash = 0x811c9dc5 ^ (seed | 0);
  for (let index = 0; index < name.length; index += 1) {
    hash ^= name.charCodeAt(index);
    // The FNV prime, by shift-add rather than multiply, to stay in int32.
    hash = (hash + ((hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24))) | 0;
  }
  return hash | 0;
}

/** A seed to hand a run that did not ask for one. Not reproducible, by design. */
export function randomSeed(): number {
  return (Math.floor(Math.random() * 0xffffffff) | 0) >>> 0;
}
